import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { Member, Role } from "@chat-yai/shared";
import { tenantsApi } from "@/api/endpoints/tenants";
import { Modal } from "@/components/Modal";
import { FormError } from "@/components/FormError";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { Icon } from "@/components/Icon";

const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  viewer: "Viewer",
  support: "Support",
};

const inviteSchema = z.object({
  email: z.string().email("Enter a valid email."),
  role: z.enum(["owner", "admin", "viewer", "support"]),
});
type InviteValues = z.infer<typeof inviteSchema>;

export function MembersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<"members" | "invitations">("members");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);

  const currentQuery = useQuery({
    queryKey: ["tenants", "current"],
    queryFn: () => tenantsApi.current(),
  });
  const role: Role = currentQuery.data?.role ?? "viewer";
  const canManage = role === "owner" || role === "admin";

  const membersQuery = useQuery({
    queryKey: ["members"],
    queryFn: () => tenantsApi.members(),
  });
  const invitationsQuery = useQuery({
    queryKey: ["invitations", "pending"],
    queryFn: () => tenantsApi.invitations({ status: "pending" }),
    enabled: canManage,
  });

  const updateRole = useMutation({
    mutationFn: ({ userId, newRole }: { userId: string; newRole: Role }) =>
      tenantsApi.updateMember(userId, { role: newRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      setEditingMember(null);
      toast.push("success", "Role updated.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => tenantsApi.removeMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      toast.push("success", "Member removed.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const revokeInvitation = useMutation({
    mutationFn: (id: string) => tenantsApi.revokeInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", "pending"] });
      toast.push("success", "Invitation revoked.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const inviteForm = useForm<InviteValues>({ defaultValues: { email: "", role: "viewer" } });
  const createInvite = useMutation({
    mutationFn: (v: InviteValues) => tenantsApi.createInvitation(inviteSchema.parse(v)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", "pending"] });
      setInviteOpen(false);
      inviteForm.reset();
      toast.push("success", "Invitation sent.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  return (
    <div className="page" data-testid="members-page">
      <div className="page-head">
        <div>
          <div className="page-title">Team</div>
          <div className="page-sub">
            Manage who has access to {currentQuery.data?.tenant.name ?? "this workspace"} and
            their permissions.
          </div>
        </div>
        {canManage ? (
          <div className="page-actions">
            <button
              className="btn btn-accent btn-sm"
              onClick={() => setInviteOpen(true)}
              data-testid="open-invite"
            >
              <Icon name="plus" size={12} /> Invite member
            </button>
          </div>
        ) : null}
      </div>

      <div className="tabs" role="tablist">
        <button
          className={"tab" + (tab === "members" ? " active" : "")}
          onClick={() => setTab("members")}
          data-testid="tab-members"
        >
          Members <span className="tab-badge">{membersQuery.data?.members.length ?? 0}</span>
        </button>
        {canManage ? (
          <button
            className={"tab" + (tab === "invitations" ? " active" : "")}
            onClick={() => setTab("invitations")}
            data-testid="tab-invitations"
          >
            Pending invitations{" "}
            <span className="tab-badge">
              {invitationsQuery.data?.invitations.length ?? 0}
            </span>
          </button>
        ) : null}
      </div>

      {tab === "members" ? (
        <div className="card">
          <div
            className="table-row-head"
            style={{ gridTemplateColumns: "2fr 1fr 1fr 100px" }}
          >
            <div>Member</div>
            <div>Role</div>
            <div>Joined</div>
            <div></div>
          </div>
          {membersQuery.data?.members.map((m) => (
            <div
              key={m.user_id}
              className="list-row"
              style={{ gridTemplateColumns: "2fr 1fr 1fr 100px" }}
              data-testid={`member-row-${m.email}`}
            >
              <div>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{m.display_name ?? m.email}</div>
                <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                  {m.email}
                </div>
              </div>
              <div>
                <span
                  className={"chip " + (m.role === "owner" ? "chip-accent" : "")}
                  style={{ textTransform: "capitalize" }}
                >
                  {ROLE_LABELS[m.role]}
                </span>
              </div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {new Date(m.joined_at).toLocaleDateString()}
              </div>
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                {canManage ? (
                  <>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setEditingMember(m)}
                      data-testid={`edit-role-${m.email}`}
                    >
                      Role
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        if (confirm(`Remove ${m.email} from this workspace?`)) {
                          removeMember.mutate(m.user_id);
                        }
                      }}
                    >
                      Remove
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          ))}
          {(membersQuery.data?.members.length ?? 0) === 0 ? (
            <div className="empty">No members yet.</div>
          ) : null}
        </div>
      ) : (
        <div className="card">
          <div
            className="table-row-head"
            style={{ gridTemplateColumns: "2fr 1fr 1fr 100px" }}
          >
            <div>Email</div>
            <div>Role</div>
            <div>Sent</div>
            <div></div>
          </div>
          {invitationsQuery.data?.invitations.map((i) => (
            <div
              key={i.id}
              className="list-row"
              style={{ gridTemplateColumns: "2fr 1fr 1fr 100px" }}
              data-testid={`invitation-row-${i.email}`}
            >
              <div className="mono" style={{ fontSize: 12 }}>
                {i.email}
              </div>
              <div>
                <span className="chip" style={{ textTransform: "capitalize" }}>
                  {ROLE_LABELS[i.role]}
                </span>
              </div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {new Date(i.created_at).toLocaleDateString()}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => revokeInvitation.mutate(i.id)}
                  data-testid={`revoke-${i.email}`}
                >
                  Revoke
                </button>
              </div>
            </div>
          ))}
          {(invitationsQuery.data?.invitations.length ?? 0) === 0 ? (
            <div className="empty">No pending invitations.</div>
          ) : null}
        </div>
      )}

      <Modal
        title="Invite member"
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setInviteOpen(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={createInvite.isPending}
              onClick={inviteForm.handleSubmit((v) => createInvite.mutate(v))}
              data-testid="submit-invite"
            >
              {createInvite.isPending ? "Sending…" : "Send invite"}
            </button>
          </>
        }
      >
        <div className="field-group">
          <label>Email</label>
          <input
            className="field-input"
            type="email"
            data-testid="invite-email"
            {...inviteForm.register("email", { required: "Email is required." })}
          />
          <FormError message={inviteForm.formState.errors.email?.message} />
        </div>
        <div className="field-group">
          <label>Role</label>
          <select className="field-input" {...inviteForm.register("role")}>
            <option value="admin">Admin · manage members and content</option>
            <option value="viewer">Viewer · read only</option>
            <option value="support">Support · respond to conversations</option>
            {role === "owner" ? <option value="owner">Owner · full control</option> : null}
          </select>
        </div>
      </Modal>

      <Modal
        title={`Change role · ${editingMember?.email ?? ""}`}
        open={!!editingMember}
        onClose={() => setEditingMember(null)}
        footer={
          <button className="btn btn-ghost btn-sm" onClick={() => setEditingMember(null)}>
            Close
          </button>
        }
      >
        {editingMember ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(["owner", "admin", "viewer", "support"] as Role[]).map((r) => (
              <button
                key={r}
                className={
                  "btn " + (editingMember.role === r ? "btn-primary" : "btn-outline")
                }
                onClick={() => updateRole.mutate({ userId: editingMember.user_id, newRole: r })}
                disabled={updateRole.isPending || (r === "owner" && role !== "owner")}
                data-testid={`role-option-${r}`}
              >
                {ROLE_LABELS[r]}
              </button>
            ))}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
